// The accountant's own postings (06 §6.3–6.5, 04 §4.6): the day's exchange rates, manual journals
// and their reversals, the month-end FX revaluation and the year-end close.
//
// FOR THE OFFICE SCREENS. Every document below is written by Cloud Functions only; clients read them
// (scope rules in `firebase/firestore.rules`) and change them only by these commands.
//
// Collections (server-written)
//   fxRates/{date}__AED__{currency}       the day's rate, "1 AED buys `rate`", with who, where from and
//                                         the earlier values of the same day (`history`)
//   manualJournals/mj_{ulid}              a manual journal: lines, status, the maker, the checker, the
//                                         journal it posted and its reversal
//   fxRevaluations/{entityId}__{YYYY-MM}  the revaluation runs of a month: rows, journals, gain or loss
//   fiscalYears/{entityId}__{YYYY}        a closed (or reopened) year: closing journal, result, history
//
// Commands:  setFxRates · postAdjustment · reverseAdjustment · revalueFx · closeYear · reopenYear
import { z } from 'zod';
import { FX_QUOTED_CURRENCIES, MANUAL_LINK_DIMENSION } from '@ecobox/domain';
import { sectorCodeSchema } from './config';
import { defineCommand } from './define';
import { accountCodeSchema, periodSchema } from './finance';
import { revaluationRowSchema } from './ledgerReports';

const minor = z.number().int().nonnegative().max(1e12);
const signedMinor = z.number().int().min(-1e13).max(1e13);
const iso = z.string();
const date = z.iso.date();
const rateText = z
  .string()
  .trim()
  .regex(/^\d{1,9}(\.\d{1,12})?$/, 'A rate like 15.7125: digits and a dot, at most 12 decimals')
  .refine((r) => /[1-9]/.test(r), 'A rate is more than zero');

// ---------------------------------------------------------------- FX rates

export const FX_RATE_SOURCES = ['bank', 'central_bank', 'provider', 'other'] as const;
export type FxRateSource = (typeof FX_RATE_SOURCES)[number];

/**
 * `setFxRates` (`finance.fx`): one to 400 dated rates against AED, typed or imported. A rate for a
 * day that already has one replaces it; the earlier value stays in the document's history. Every
 * report and recharge reads `fxRates` (06 §6.5, 14 §14.9) and never takes a missing rate as 1:1.
 */
export const setFxRates = defineCommand({
  type: 'setFxRates',
  permission: 'finance.fx',
  principals: [],
  offline: false,
  payload: z
    .object({
      rates: z
        .array(
          z
            .object({
              date,
              currency: z.enum(FX_QUOTED_CURRENCIES),
              /** 1 AED buys this much of `currency`. */
              rate: rateText,
            })
            .strict(),
        )
        .min(1)
        .max(400),
      source: z.enum(FX_RATE_SOURCES),
      /** Where the rate came from in words: "SAMA reference rate", "Emirates NBD board rate". */
      sourceName: z.string().trim().min(2).max(80),
      note: z.string().max(300).default(''),
    })
    .strict()
    .refine((v) => new Set(v.rates.map((r) => `${r.date} ${r.currency}`)).size === v.rates.length, {
      message: 'A currency appears twice for the same day',
      path: ['rates'],
    }),
});

/** `fxRates/{date}__AED__{currency}`. The first four fields are what every reader of rates needs. */
export const fxRateDocSchema = z
  .object({
    from: z.string(),
    to: z.string(),
    date,
    rate: z.string(),
    source: z.string(),
    sourceName: z.string().optional(),
    enteredBy: z.string().optional(),
    enteredAt: iso.optional(),
    commandId: z.string().optional(),
    note: z.string().optional(),
    /** Change against the latest earlier rate of the pair, in basis points; flagged above 500. */
    changeBp: z.number().int().nonnegative().nullable().optional(),
    history: z
      .array(
        z.object({
          rate: z.string(),
          source: z.string(),
          by: z.string().optional(),
          at: iso.optional(),
          commandId: z.string().optional(),
        }),
      )
      .max(50)
      .optional(),
  })
  .passthrough();
export type FxRateDoc = z.infer<typeof fxRateDocSchema>;

// ---------------------------------------------------------- manual journals

export const MANUAL_LINK_KINDS = Object.keys(MANUAL_LINK_DIMENSION) as [
  keyof typeof MANUAL_LINK_DIMENSION,
  ...(keyof typeof MANUAL_LINK_DIMENSION)[],
];

export const manualLineSchema = z
  .object({
    accountCode: accountCodeSchema,
    debitMinor: minor.default(0),
    creditMinor: minor.default(0),
    description: z.string().trim().max(120).default(''),
    /** On a wallet account (1000, 1001, 1005, 1010, 1015, 1020): the wallet. */
    walletId: z.string().min(1).max(60).optional(),
    link: z
      .object({ kind: z.enum(MANUAL_LINK_KINDS), id: z.string().trim().min(1).max(60) })
      .strict()
      .optional(),
  })
  .strict();
export type ManualLine = z.infer<typeof manualLineSchema>;

/**
 * `postAdjustment` (`finance.adjust`, 04 §4.6): a balanced manual journal of one country, dated in an
 * open month, with the reason. It always waits for a second person with `finance.approve` (never the
 * maker); approving posts it, `{entity}__manual__{mj_…}__v1`, numbered by the ledger like every journal.
 */
export const postAdjustment = defineCommand({
  type: 'postAdjustment',
  permission: 'finance.adjust',
  principals: [],
  offline: false,
  payload: z
    .object({
      entityId: sectorCodeSchema,
      date,
      memo: z.string().trim().min(10).max(300),
      lines: z.array(manualLineSchema).min(2).max(40),
      attachmentPath: z.string().max(200).optional(),
    })
    .strict(),
});

/** `reverseAdjustment` (`finance.adjust`): the mirror of a posted manual journal, dated in an open month; approved like the original. */
export const reverseAdjustment = defineCommand({
  type: 'reverseAdjustment',
  permission: 'finance.adjust',
  principals: [],
  offline: false,
  payload: z
    .object({
      manualJournalId: z.string().regex(/^mj_[0-9A-HJKMNP-TV-Z]{26}$/),
      date,
      reason: z.string().trim().min(10).max(300),
    })
    .strict(),
});

export const MANUAL_JOURNAL_STATUSES = [
  'awaiting_approval',
  'posted',
  'rejected',
  'reversal_pending',
  'reversed',
] as const;
export type ManualJournalStatus = (typeof MANUAL_JOURNAL_STATUSES)[number];

/** `manualJournals/mj_{ulid}`. */
export const manualJournalDocSchema = z.object({
  id: z.string(),
  entityId: sectorCodeSchema,
  currency: z.string(),
  date,
  period: periodSchema,
  memo: z.string(),
  lines: z.array(
    z.object({
      accountCode: accountCodeSchema,
      debitMinor: minor,
      creditMinor: minor,
      description: z.string(),
      walletId: z.string().optional(),
      linkKind: z.string().optional(),
      linkId: z.string().optional(),
    }),
  ),
  totalMinor: minor,
  status: z.enum(MANUAL_JOURNAL_STATUSES),
  makerUid: z.string(),
  commandId: z.string(),
  approvalId: z.string(),
  attachmentPath: z.string().optional(),
  journalId: z.string().optional(),
  approvedBy: z.string().optional(),
  postedAt: iso.optional(),
  /** The posted journal's date: the requested one. */
  businessDate: date.optional(),
  rejectedBy: z.string().optional(),
  reversal: z
    .object({
      date,
      reason: z.string(),
      requestedBy: z.string(),
      commandId: z.string(),
      approvalId: z.string(),
      journalId: z.string().optional(),
      approvedBy: z.string().optional(),
      rejectedBy: z.string().optional(),
    })
    .optional(),
  createdAt: iso,
  updatedAt: iso,
});
export type ManualJournalDoc = z.infer<typeof manualJournalDocSchema>;

// ------------------------------------------------------------ FX revaluation

/**
 * `revalueFx` (`finance.fx`, 06 §6.3): restates the country's foreign-currency balances (1200 and 2100
 * with a country whose currency they are owed in) at the closing rate of the month. Posts
 * `{entity}__fx_revaluation__{YYYY-MM}-{n}__v1` on the month's last day (Dr/Cr 1200, 2100, the net to
 * 5200) and its reversal on the first day of the next month. The month must be over and open.
 */
export const revalueFx = defineCommand({
  type: 'revalueFx',
  permission: 'finance.fx',
  principals: [],
  offline: false,
  payload: z.object({ entityId: sectorCodeSchema, period: periodSchema }).strict(),
});

/** `fxRevaluations/{entityId}__{YYYY-MM}`. */
export const fxRevaluationDocSchema = z.object({
  entityId: sectorCodeSchema,
  period: periodSchema,
  currency: z.string(),
  runs: z
    .array(
      z.object({
        run: z.number().int().positive(),
        journalId: z.string().nullable(),
        reversalJournalId: z.string().nullable(),
        gainMinor: signedMinor,
        rows: z.array(revaluationRowSchema),
        by: z.string(),
        at: iso,
        commandId: z.string(),
      }),
    )
    .max(50),
  updatedAt: iso,
});
export type FxRevaluationDoc = z.infer<typeof fxRevaluationDocSchema>;

// ------------------------------------------------------------ year-end close

const yearSchema = z.number().int().min(2020).max(2100);

/**
 * `closeYear` (`finance.period.close`): after every month of the year is closed, posts the closing
 * journal (`{entity}__year_end__{YYYY}-{n}__v1`, dated 31 December: every P&L account to zero, the
 * result to 3200) and locks the year: no month of it can be reopened until the year is.
 */
export const closeYear = defineCommand({
  type: 'closeYear',
  permission: 'finance.period.close',
  principals: [],
  offline: false,
  payload: z
    .object({ entityId: sectorCodeSchema, year: yearSchema, note: z.string().max(300).default('') })
    .strict(),
});

/** `reopenYear`: a request a second person with `finance.approve` decides; approving reverses the closing journal. */
export const reopenYear = defineCommand({
  type: 'reopenYear',
  permission: 'finance.period.close',
  principals: [],
  offline: false,
  payload: z
    .object({
      entityId: sectorCodeSchema,
      year: yearSchema,
      reason: z.string().trim().min(10).max(300),
    })
    .strict(),
});

/** `fiscalYears/{entityId}__{YYYY}`. Absent = the year was never closed. */
export const fiscalYearDocSchema = z.object({
  entityId: sectorCodeSchema,
  year: z.number().int(),
  currency: z.string(),
  status: z.enum(['open', 'closed']),
  /** How many closing journals were posted (each reopen reverses the last one). */
  closeCount: z.number().int().nonnegative(),
  closingJournalId: z.string().nullable(),
  netResultMinor: signedMinor,
  /** What the closing journals in force moved, per account: taken out of December's P&L. */
  closingLines: z.record(accountCodeSchema, z.object({ debit: minor, credit: minor })),
  closedAt: iso.optional(),
  closedBy: z.string().optional(),
  pendingReopenApprovalId: z.string().nullable().optional(),
  history: z.array(
    z.object({
      action: z.enum(['closed', 'reopen_requested', 'reopened']),
      by: z.string(),
      at: iso,
      reason: z.string().optional(),
      approvalId: z.string().optional(),
      approvedBy: z.string().optional(),
      journalId: z.string().optional(),
    }),
  ),
  updatedAt: iso,
});
export type FiscalYearDoc = z.infer<typeof fiscalYearDocSchema>;

export const FINANCE_CORE_COMMANDS = {
  setFxRates,
  postAdjustment,
  reverseAdjustment,
  revalueFx,
  closeYear,
  reopenYear,
} as const;
